/**
 * הגדרת תפקידים — the customer DEFINING their own roles, not merely choosing from five.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * OA-0001 §3.2 WAS HALF TRUE UNTIL THIS SCREEN EXISTED
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 *   > *"GL-POINT supplies the authorization system; the customer OPERATES it."*
 *
 * An office could ASSIGN the five seeded roles and could not DEFINE a sixth. An office whose shape
 * did not match those five — a bookkeeper who may see identifiers but not create companies, a
 * part-time person with billing read and nothing else — had no move at all except to over-grant, and
 * over-granting is what every one of these rules exists to prevent.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * WHY THIS IS NOT A PANEL ON THE ASSIGNMENT SCREEN
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `TenantRolesScreen` must offer NO capability control. OA-0001 §3.7: *"they do not collapse into one
 * admin flag"*, and a control that granted every capability at once would be that flag wearing a
 * Hebrew label. `adminScreens.test.ts` counts the controls on that screen so it cannot appear.
 *
 * This screen is nothing BUT a capability list — because that is what a role IS. Putting the two on
 * one page would leave a picker that grants every capability at once one scroll below a picker that
 * grants a role, and the count test that guards the first would have to be relaxed to allow it.
 * Splitting them keeps the guard exact.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE CEILING APPLIES HERE MORE SHARPLY THAN IT DOES TO ASSIGNMENT
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `tenant-lifecycle.ts` names the reason: *a bounded administrator does not need to assign anything —
 * they edit a role they already hold.* Authoring is the SHORTER ESCALATION PATH, so the same
 * delegation ceiling that bounds assignment bounds this, and the refusal carries the same `excess`
 * list rendered by the same component. Nothing on this screen evaluates a ceiling; it states the rule
 * and renders whatever the server decided.
 *
 * ── EDITING REPLACES THE SET, AND THE FORM SAYS SO ─────────────────────────────────────────────
 *
 * The update route replaces a role's capability list. So choosing a role LOADS ITS CURRENT SET — a
 * form that started empty would turn "add one capability" into "remove the other fifteen" on the most
 * sensitive write an office has.
 *
 * ── AND AN EMPTY ROLE IS VALID ─────────────────────────────────────────────────────────────────
 *
 * A role that confers nothing is how an office models a job title before deciding what it may do. It
 * is also the only role a principal with an empty ceiling can author, which is the correct floor
 * rather than an edge case, and the screen neither blocks it nor warns about it.
 *
 * ── NO ROLE HERE GRANTS A COMPANY ──────────────────────────────────────────────────────────────
 *
 * D5 / CG-1, restated because this is where somebody composing a role will look for it: there is no
 * company on this form and no capability in the catalogue that confers one. Company access is a
 * separate table, a separate capability and a separate screen.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { RoleRefusalNotice } from '../components/RoleRefusalNotice';
import { StateBlock } from '../components/StateBlock';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { RoleAssignmentRefusalReason } from '../api/contract';
import { ROLE_ASSIGNMENT_REFUSALS } from '../api/contract';
import { capabilityDescriptionHe, describedCapabilityCodes } from '../api/adminVocabulary';
import { REFUSED_WITHOUT_DETAIL_HE } from '../api/roleAssignmentText';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { fixtureRoles, fixturesEnabled } from '../api/fixtures';
import { routes } from '../routes';

const TITLE_HE = 'הגדרת תפקידים';

/** `''` ⇒ composing a NEW role. Otherwise the code of the role being edited. */
const NEW_ROLE = '';

type Outcome =
  | { readonly kind: 'saved'; readonly created: boolean; readonly capabilityCount: number }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

function knownRefusal(reason: string): RoleAssignmentRefusalReason | null {
  const known: readonly string[] = ROLE_ASSIGNMENT_REFUSALS;
  return known.includes(reason) ? (reason as RoleAssignmentRefusalReason) : null;
}

export function TenantRoleAuthoringScreen(): JSX.Element {
  const ctx = useWorkContext();
  const [editing, setEditing] = useState<string>(NEW_ROLE);
  const [roleCode, setRoleCode] = useState('');
  const [displayNameHe, setDisplayNameHe] = useState('');
  const [capabilities, setCapabilities] = useState<readonly string[]>([]);
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const roles = fixturesEnabled() ? fixtureRoles : [];

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="TENANT" actual={ctx.surface} />;
  }

  /** The catalogue this build has Hebrew for. A code with no Hebrew is never offered as a control. */
  const catalogue = describedCapabilityCodes();

  function choose(code: string): void {
    setEditing(code);
    setOutcome(null);
    if (code === NEW_ROLE) {
      setRoleCode('');
      setDisplayNameHe('');
      setCapabilities([]);
      return;
    }
    const role = roles.find((r) => r.roleCode === code);
    if (role === undefined) return;
    setRoleCode(role.roleCode);
    setDisplayNameHe(role.displayNameHe);
    // THE CURRENT SET, because the route replaces it. See the header.
    setCapabilities(role.capabilities);
  }

  function toggle(code: string): void {
    setCapabilities((current) =>
      current.includes(code) ? current.filter((c) => c !== code) : [...current, code],
    );
  }

  function submit(e: FormEvent): void {
    e.preventDefault();
    setSending(true);
    setOutcome(null);
    const isNew = editing === NEW_ROLE;
    void (async () => {
      try {
        if (isNew) {
          await callOperation('tenant.role.create', {
            body: { roleCode, displayNameHe, capabilities },
          });
        } else {
          // The role code comes from the ADDRESS the request is made on and is NOT re-read from the
          // body: a body that could rename the role being edited is a body that can edit a different
          // role than the URL names, and the audit row records the URL.
          await callOperation('tenant.role.update', {
            params: { role_code: editing },
            body: { displayNameHe, capabilities },
          });
        }
        setOutcome({ kind: 'saved', created: isNew, capabilityCount: capabilities.length });
      } catch (err: unknown) {
        setOutcome({ kind: 'failed', failure: classifyActionError(err) });
      } finally {
        setSending(false);
      }
    })();
  }

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'תפקידים והרשאות', href: routes.tenantRoles }}
      provenanceHe="רשימת ההרשאות היא קטלוג ההרשאות של המערכת כלשונו. המסך אינו ממציא שמות ואינו מציג הרשאה שאין לה שם בעברית."
    >
      <section className="notice" data-tone="info">
        <h2 className="notice__title">תפקיד הוא רשימת ההרשאות שמתחתיו</h2>
        <p className="notice__body">
          הגדרת תפקיד קובעת מה מותר למי שמחזיק בו לעשות. היא אינה מקנה גישה לאף חברה — גם תפקיד שכולל
          את כל ההרשאות אינו מקנה גישה ולו לחברה אחת. גישה לחברה היא הענקה נפרדת, לכל חברה בנפרד.
        </p>
        <p className="notice__body">
          אפשר לכלול בתפקיד רק הרשאות שקיימות בתפקידים שלך. הרשאות שקובעות מי מקבל הרשאות שמורות
          לבעלים של המשרד — אחרת אפשר היה להרחיב את הגבול שנקבע לתפקיד בעריכה של תפקיד קיים, בלי
          להעניק דבר לאיש.
        </p>
        <p className="notice__body">
          תפקיד ללא הרשאות הוא הגדרה תקינה. כך מגדירים תפקיד לפני שמחליטים מה מותר בו.
        </p>
        {/*
          Stated at the top and not only beside the control, because it is the sentence somebody
          skips: editing an existing role REPLACES its capability list, and the change reaches every
          person who holds that role at once.
        */}
        <p className="notice__body">
          בעריכת תפקיד קיים, רשימת ההרשאות שלו תוחלף בדיוק במה שמסומן כאן — הרשאה שסימונה יוסר תוסר
          מכל מי שמחזיק בתפקיד, מיד.
        </p>
      </section>

      <form className="form" onSubmit={submit}>
        <fieldset className="field field--choice">
          <legend className="field__label">מה מגדירים</legend>
          <label className="choice">
            <input
              type="radio"
              name="authoringTarget"
              value={NEW_ROLE}
              checked={editing === NEW_ROLE}
              onChange={() => choose(NEW_ROLE)}
            />
            <span>
              תפקיד חדש
              <span className="choice__hint">מוסיף תפקיד לרשימת התפקידים של המשרד.</span>
            </span>
          </label>
          {roles.map((role) => (
            <label key={role.roleCode} className="choice">
              <input
                type="radio"
                name="authoringTarget"
                value={role.roleCode}
                checked={editing === role.roleCode}
                onChange={() => choose(role.roleCode)}
              />
              <span>
                {role.displayNameHe}
                <span className="choice__hint">
                  {`מקנה כיום ${role.capabilities.length} הרשאות`}
                </span>
              </span>
            </label>
          ))}
        </fieldset>

        {editing === NEW_ROLE ? (
          <label className="field">
            <span className="field__label">מזהה התפקיד</span>
            <input
              className="field__input"
              name="roleCode"
              dir="ltr"
              value={roleCode}
              onChange={(e) => setRoleCode(e.currentTarget.value)}
            />
            <span className="field__hint">
              מזהה קבוע לתפקיד. הוא אינו משתנה לאחר ההגדרה, כדי שרשומות ביומן ימשיכו להצביע עליו.
            </span>
          </label>
        ) : (
          <p className="form__note">
            מזהה התפקיד אינו ניתן לשינוי בעריכה. שינוי שלו היה מנתק את הרשומות ביומן מהתפקיד שהן
            מתארות.
          </p>
        )}

        <label className="field">
          <span className="field__label">שם התפקיד בעברית</span>
          <input
            className="field__input"
            name="displayNameHe"
            value={displayNameHe}
            onChange={(e) => setDisplayNameHe(e.currentTarget.value)}
          />
          <span className="field__hint">השם שמופיע לצד כל משתמש שמחזיק בתפקיד.</span>
        </label>

        <fieldset className="field field--choice">
          <legend className="field__label">ההרשאות שהתפקיד מקנה</legend>
          <div className="roles">
            {catalogue.map((code) => (
              <label key={code} className="role__choice">
                <input
                  type="checkbox"
                  name="capabilities"
                  value={code}
                  checked={capabilities.includes(code)}
                  onChange={() => toggle(code)}
                />
                {/* The catalogue's own Hebrew, verbatim. A code never appears (09 §9.1 V3). */}
                <span className="role__title">{capabilityDescriptionHe(code)}</span>
              </label>
            ))}
          </div>
          <span className="field__hint">
            {editing === NEW_ROLE
              ? 'מה שמסומן כאן הוא מה שהתפקיד יקנה.'
              : 'רשימת ההרשאות של התפקיד תוחלף בדיוק במה שמסומן כאן. הרשאה שסימונה יוסר — תוסר מכל מי שמחזיק בתפקיד.'}
          </span>
        </fieldset>

        <div className="actions-row">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={
              sending || displayNameHe === '' || (editing === NEW_ROLE && roleCode === '')
            }
          >
            {sending
              ? 'שולח…'
              : editing === NEW_ROLE
                ? 'הגדרת התפקיד'
                : 'שמירת השינוי בתפקיד'}
          </button>
        </div>
        <p className="form__note">
          ההגדרה נרשמת ביומן הביקורת יחד עם מי שביצע אותה. שינוי בתפקיד קיים חל מיד על כל מי שמחזיק
          בו.
        </p>
      </form>

      {outcome === null ? null : outcome.kind === 'saved' ? (
        <StateBlock
          titleHe={outcome.created ? 'התפקיד הוגדר' : 'התפקיד עודכן'}
          bodyHe={
            outcome.capabilityCount === 0
              ? 'התפקיד נשמר ואינו מקנה הרשאות. זו הגדרה תקינה, ואפשר להשלים אותה מאוחר יותר.'
              : `התפקיד נשמר ומקנה ${outcome.capabilityCount} הרשאות. הוא אינו מקנה גישה לאף חברה.`
          }
          nextStep={
            <a className="btn btn--primary" href={routes.tenantRoles}>
              מעבר להענקת התפקיד למשתמש
            </a>
          }
        />
      ) : (
        <ActionFailureNotice
          failure={outcome.failure}
          renderConflict={(failure) => {
            const reason = knownRefusal(failure.body.error);
            if (reason === null) {
              return (
                <StateBlock
                  titleHe={REFUSED_WITHOUT_DETAIL_HE.titleHe}
                  bodyHe={REFUSED_WITHOUT_DETAIL_HE.bodyHe}
                  tone="error"
                  nextStep={<p>{REFUSED_WITHOUT_DETAIL_HE.nextStepHe}</p>}
                />
              );
            }
            // The same ceiling, the same list, the same component as on the assignment screen —
            // because it is the same rule, and two renderings of it would drift.
            return <RoleRefusalNotice refusal={{ reason, excess: failure.body.excess ?? [] }} />;
          }}
        />
      )}
    </AppShell>
  );
}
